import { shrunkSize } from '@core/api/mediaUpload';

/** The types a browser can draw onto a canvas. HEIC is not among them outside Safari. */
const drawable = ['image/jpeg', 'image/png', 'image/webp'];

/**
 * A catalogue photograph made small enough to send — its longest side brought down to the core's
 * 1600 pixels, the size the phone's camera takes them at.
 *
 * <p>
 * <strong>It only ever falls back to the original.</strong> A clip, a HEIC the browser cannot
 * decode, a photograph already within the size, a browser without `createImageBitmap`, and a
 * re-encode that came out <em>bigger</em> all send the file as chosen. The server's own limits still
 * apply to whatever is sent, so the worst a failure here can do is send a larger file than needed.
 * </p>
 *
 * <p>
 * Drawn with the photograph's own orientation applied (`from-image`), because the canvas drops EXIF:
 * a portrait taken on a phone held upright would otherwise arrive on its side. JPEG has no
 * transparency, so a PNG's clear parts are filled white, as a page would show them.
 * </p>
 */
export async function shrinkPhoto(file: File): Promise<File> {
  if (!drawable.includes(file.type.toLowerCase()) || typeof createImageBitmap !== 'function') {
    return file;
  }

  let bitmap: ImageBitmap;

  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    return file;
  }

  try {
    const size = shrunkSize(bitmap.width, bitmap.height);

    if (!size) {
      return file;
    }

    const canvas = document.createElement('canvas');
    canvas.width = size.width;
    canvas.height = size.height;

    const context = canvas.getContext('2d');

    if (!context) {
      return file;
    }

    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, size.width, size.height);
    context.imageSmoothingQuality = 'high';
    context.drawImage(bitmap, 0, 0, size.width, size.height);

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', 0.85),
    );

    if (!blob || blob.size >= file.size) {
      return file;
    }

    return new File([blob], `${file.name.replace(/\.[^.]*$/, '')}.jpg`, {
      type: 'image/jpeg',
      lastModified: file.lastModified,
    });
  } finally {
    bitmap.close();
  }
}
